// src/encounter/wrap-up-invoice-section.tsx
// WrapUpInvoiceSection — the Wrap-Up step's INVOICE block.
//
// 2026-09-22 (PA→Signal DS consumption): this file used to hand-compose the whole
// title-row/diagnosis-grid/services-table/totals-panel anatomy against tokens (Flutter
// `invoice_services_widget.dart` as source). That render is now owned by the DS organism
// `WrapUpInvoicePanel` — built as a faithful P2 port of THIS file (see its own docblock,
// `@scripsteam/scrips-signal-ds` `dist/organisms/WrapUpInvoicePanel/WrapUpInvoicePanel.d.ts`),
// carrying the same colour-role decisions verbatim (scripsTitleBlueColor → --color-brand-depth,
// the DX_TITLE/DX_DESC nearest-token calls, the invoice tile's --color-status-warning seat,
// etc. — recorded human decisions, not re-litigated here or there).
//
// `InvoiceSectionData` (`./lib/to-invoice-section`) and the DS's own `WrapUpInvoiceData` are
// the SAME shape (sponsored/insurance/draft/currencyCode/dx/rows/totals, same field names on
// every row) by construction — the DS type was lifted from this file's own view model — so this
// component is now a passthrough: no reshaping, no local rendering. The handler prop names are
// identical 1:1 (`onAddService`, `onPreviewInvoice`, `onRemoveService`, `onEditService`, `busy`,
// `onEditManagementDiscount`, `onCoverageChange`, `onAuthCodeChange`, `onServiceDateChange`,
// `onValidityChange`) — the page's existing wiring is unchanged.
import { WrapUpInvoicePanel } from '@scripsteam/scrips-signal-ds';
import type { InvoiceCoverage, InvoiceSectionData } from './lib/to-invoice-section';
import { InvoiceActions, InvoiceBlock } from './invoice-block';

export interface WrapUpInvoiceSectionProps {
  /** Mapped invoice. `null` → Flutter's "Invoice not created" state (wrap_up_billing_screen:777). */
  data: InvoiceSectionData | null;
  /** Add a service. Flutter gates the affordance on canAddInvoiceProcedure() && draft (:793). */
  onAddService?: () => void;
  /** Preview the invoice (Flutter opens InvoicePopUp, :806-814). */
  onPreviewInvoice?: () => void;
  /** Remove a service line (Flutter `removeServices`, :825-835). */
  onRemoveService?: (lineId: string) => void;
  /** Edit a service line — Flutter shows this only on a SELF-PAY invoice (the sponsored
   *  variant's row actions are delete-only; see the two live screenshots). */
  onEditService?: (lineId: string) => void;
  /** Disables every mutating affordance while a write is in flight. */
  busy?: boolean;
  /**
   * Open the invoice-wide management-discount dialog, only on a SELF-PAY invoice that is not yet
   * ISSUED (invoice_services_widget.dart:852-890). The app's InvoiceBlock puts it on the Net Total
   * row (its totals follow the Practice app, which shows no Patient Payable for self-pay). Absent
   * → the rows render as plain text, exactly as they do for a sponsored invoice.
   */
  onEditManagementDiscount?: () => void;
  /**
   * COVERAGE change on a sponsored line. Flutter renders a dropdown whose options are
   * ['Included', 'Excluded'] plus 'Needs approval' on an INSURANCE invoice
   * (invoice_services_widget.dart:97-106, app_constants.dart:753-755), each mapping to its own
   * AddRemoveService array — `includedServices` / `excludedServices` / `needsApprovalServices`
   * (:640-643, wrap_up_billing_screen.dart:847-879). Absent → the cell renders read-only text.
   */
  onCoverageChange?: (lineId: string, coverage: InvoiceCoverage) => void;
  /** AUTH CODE edit — Flutter's inline field committing `updateAuthCode[{id, authCode}]`
   *  (:496-514 → wrap_up_billing_screen.dart:880-894). Absent → read-only text. */
  onAuthCodeChange?: (lineId: string, authCode: string) => void;
  /** SERVICE DATE — `updateServiceDate[{id, serviceAddedDate}]` (wrap_up_billing_screen:895-909). */
  onServiceDateChange?: (lineId: string, serviceAddedDate: string) => void;
  /**
   * VALIDITY PERIOD start — `updateValidity[{id, validityStartDate, validityEndDate}]`
   * (:925-939). Flutter defaults the END to the START when only a start is set, so this single
   * picker is the whole editor for the common case; the host applies that same default.
   * Backed by real columns: Billing `InvoiceDetailsDto.cs:23-24` (verified 2026-08-06).
   */
  onValidityChange?: (lineId: string, validityStartDate: string) => void;
  /**
   * Frame v2 (export 21-10 EncounterWrapUp): the self-pay invoice is a titled block whose Add service and Preview invoice sit in the
   * block's action row (InvoiceActions, the same handlers); the invoice body draws no header of its own. Absent: unchanged.
   */
  embedded?: boolean;
}

export function WrapUpInvoiceSection({
  data,
  onAddService,
  onPreviewInvoice,
  onRemoveService,
  onEditService,
  busy,
  onEditManagementDiscount,
  onCoverageChange,
  onAuthCodeChange,
  onServiceDateChange,
  onValidityChange,
  embedded,
}: WrapUpInvoiceSectionProps) {
  if (!data?.sponsored && embedded) {
    return (
      <div data-slot="wrap-up-invoice-block" className="flex flex-col gap-4">
        <div data-slot="wrap-up-invoice-head" className="flex items-center justify-between gap-3">
          <h3 className="m-0">Invoice</h3>
          <div data-slot="wrap-up-invoice-actions" className="flex items-center gap-2">
            <InvoiceActions data={data} onAddService={onAddService} onPreviewInvoice={onPreviewInvoice} busy={busy} />
          </div>
        </div>
        <InvoiceBlock
          data={data}
          onAddService={onAddService}
          onPreviewInvoice={onPreviewInvoice}
          onRemoveService={onRemoveService}
          onEditService={onEditService}
          busy={busy}
          onEditManagementDiscount={onEditManagementDiscount}
          embedded
        />
      </div>
    );
  }
  // 19 Oct (Antonin, "make the invoice look more like an invoice"): a self-pay (or not-created) invoice is drawn by the app's own
  // InvoiceBlock, as an invoice, on the same data and handlers. The SPONSORED variant (coverage, auth code, service date, validity
  // editors) keeps the DS panel until it gets the same drawing (a gap, listed in the changelog).
  if (!data?.sponsored) {
    return (
      <InvoiceBlock
        data={data}
        onAddService={onAddService}
        onPreviewInvoice={onPreviewInvoice}
        onRemoveService={onRemoveService}
        onEditService={onEditService}
        busy={busy}
        onEditManagementDiscount={onEditManagementDiscount}
      />
    );
  }
  return (
    <WrapUpInvoicePanel
      data={data}
      onAddService={onAddService}
      onPreviewInvoice={onPreviewInvoice}
      onRemoveService={onRemoveService}
      onEditService={onEditService}
      busy={busy}
      onEditManagementDiscount={onEditManagementDiscount}
      onCoverageChange={onCoverageChange}
      onAuthCodeChange={onAuthCodeChange}
      onServiceDateChange={onServiceDateChange}
      onValidityChange={onValidityChange}
    />
  );
}
